import { useEffect, useState } from "react";
import { X, ExternalLink, CheckCircle2 } from "lucide-react";
import { clientPortalApi } from "./clientPortalApi";

function embedUrl(url, platform) {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{6,})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vm = url.match(/vimeo\.com\/(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  return null;
}

function formatTs(seconds) {
  const s = Math.max(0, Math.floor(seconds || 0));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

function parseTs(str) {
  const parts = (str || "").trim().split(":");
  if (parts.length === 2) return (parseInt(parts[0], 10) || 0) * 60 + (parseInt(parts[1], 10) || 0);
  return parseInt(str, 10) || 0;
}

/** Client Portal M2 — the Design Vision's hero interaction: the video/link centerpiece, a
 * premium timestamped-feedback thread, and "Approve this cut" (confirm-gated, visible
 * approved state, PM notified via a Dashboard "needs attention" item server-side). */
export function DeliverablePlayer({ projectId, deliverable, onClose }) {
  const [feedback, setFeedback] = useState([]);
  const [approval, setApproval] = useState(null);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState("");
  const [ts, setTs] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [confirmApprove, setConfirmApprove] = useState(false);
  const [approving, setApproving] = useState(false);

  useEffect(() => {
    let active = true;
    clientPortalApi
      .getDeliverableFeedback(projectId, deliverable.id)
      .then((r) => {
        if (!active) return;
        setFeedback(r.data.items || []);
        setApproval(r.data.approval);
      })
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [projectId, deliverable.id]);

  // Accessibility (2026-02): Escape closes the modal, same as clicking the backdrop/X — no
  // focus trap library added for this v1 pass (kept in scope: keyboard escape + a labelled
  // dialog role), matches the "small, non-generic set of fixes" scope of the M6 a11y pass.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const embed = embedUrl(deliverable.url, deliverable.platform);

  const submitFeedback = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setSubmitting(true);
    try {
      const r = await clientPortalApi.addDeliverableFeedback(projectId, deliverable.id, body.trim(), parseTs(ts));
      setFeedback((f) => [...f, r.data.feedback].sort((a, b) => (a.timestamp_seconds || 0) - (b.timestamp_seconds || 0)));
      setBody("");
      setTs("");
    } finally {
      setSubmitting(false);
    }
  };

  const doApprove = async (val) => {
    setApproving(true);
    try {
      const r = await clientPortalApi.approveDeliverable(projectId, deliverable.id, val);
      setApproval(r.data.approval);
      setConfirmApprove(false);
    } finally {
      setApproving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 cp-enter" data-testid="client-portal-deliverable-modal" role="dialog" aria-modal="true" aria-label={`Feedback and approval for ${deliverable.title || "Deliverable"}`} onClick={onClose}>
      <div className="cp-card w-full max-w-[880px] max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4 p-6 border-b border-[color:var(--cp-border-hairline)]">
          <div className="min-w-0">
            <div className="cp-eyebrow text-[10px] mb-1">
              {deliverable.platform}
              {deliverable.version_name ? ` · ${deliverable.version_name}` : ""}
            </div>
            <h3 className="cp-display text-[22px] text-white truncate">{deliverable.title || "Untitled cut"}</h3>
          </div>
          <button onClick={onClose} data-testid="client-portal-deliverable-modal-close" aria-label="Close" className="text-[#6E6B66] hover:text-white transition-colors shrink-0">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {embed ? (
            <div className="aspect-video rounded-lg overflow-hidden bg-black">
              <iframe src={embed} title={deliverable.title} className="w-full h-full" allow="autoplay; fullscreen" allowFullScreen data-testid="client-portal-deliverable-embed" />
            </div>
          ) : (
            <a
              href={deliverable.url}
              target="_blank"
              rel="noreferrer"
              data-testid="client-portal-deliverable-open-external"
              className="cp-btn-primary flex items-center justify-center gap-2 py-4 rounded-lg text-sm"
            >
              <ExternalLink className="h-4 w-4" /> Watch on {deliverable.platform || "external site"}
            </a>
          )}

          <div className="border-t border-[color:var(--cp-border-hairline)] pt-6">
            {approval?.approved ? (
              <div className="flex items-center justify-between flex-wrap gap-3" data-testid="client-portal-approved-banner">
                <div className="flex items-center gap-2 text-sm text-[color:var(--cp-gold)]">
                  <CheckCircle2 className="h-4 w-4" /> Approved by {approval.approved_by_name}
                  {approval.approved_at ? ` on ${new Date(approval.approved_at).toLocaleDateString()}` : ""}
                </div>
                <button onClick={() => doApprove(false)} disabled={approving} data-testid="client-portal-unapprove-btn" className="text-xs text-[#6E6B66] hover:text-white transition-colors">
                  Un-approve
                </button>
              </div>
            ) : confirmApprove ? (
              <div className="cp-card p-4 bg-[color:var(--cp-graphite)]" data-testid="client-portal-approve-confirm-panel">
                <p className="text-sm text-white mb-3">Approve this cut? Your Project Manager will be notified.</p>
                <div className="flex gap-3">
                  <button onClick={() => doApprove(true)} disabled={approving} data-testid="client-portal-approve-confirm-btn" className="cp-btn-primary px-5 py-2 text-xs">
                    {approving ? "Approving…" : "Yes, approve"}
                  </button>
                  <button onClick={() => setConfirmApprove(false)} data-testid="client-portal-approve-cancel-btn" className="cp-btn-secondary px-5 py-2 text-xs">
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button onClick={() => setConfirmApprove(true)} data-testid="client-portal-approve-btn" className="cp-btn-primary inline-flex items-center gap-2 px-6 py-3 text-sm">
                <CheckCircle2 className="h-4 w-4" /> Approve this cut
              </button>
            )}
          </div>

          <div className="border-t border-[color:var(--cp-border-hairline)] pt-6">
            <span className="cp-eyebrow block mb-4">Feedback</span>
            {loading ? (
              <div className="h-16 rounded bg-white/5 animate-pulse" />
            ) : feedback.length === 0 ? (
              <p className="text-sm text-[#6E6B66] mb-4" data-testid="client-portal-feedback-empty">No feedback yet. Leave a note on a specific moment.</p>
            ) : (
              <div className="space-y-3 mb-4" data-testid="client-portal-feedback-list">
                {feedback.map((f) => (
                  <div key={f.id} className="flex gap-3" data-testid={`client-portal-feedback-item-${f.id}`}>
                    <span className="shrink-0 text-[11px] font-mono text-[color:var(--cp-gold)] bg-[color:var(--cp-graphite)] rounded px-2 py-1 h-fit">{formatTs(f.timestamp_seconds)}</span>
                    <div>
                      <div className="text-xs text-[#6E6B66] mb-0.5">{f.author_name}</div>
                      <p className="text-sm text-[#E7E5E1]">{f.body}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <form onSubmit={submitFeedback} className="flex gap-2 items-start">
              <input value={ts} onChange={(e) => setTs(e.target.value)} placeholder="0:00" data-testid="client-portal-feedback-timestamp-input" className="cp-input w-20 px-2 py-2 text-xs shrink-0" />
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Add feedback on this cut…"
                rows={2}
                data-testid="client-portal-feedback-textarea"
                className="cp-input flex-1 px-3 py-2 text-sm resize-none"
              />
              <button type="submit" disabled={submitting || !body.trim()} data-testid="client-portal-feedback-submit-btn" className="cp-btn-primary px-4 py-2 text-xs shrink-0">
                Send
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
